import React, { useState, useEffect, useCallback } from 'react';
import { StudentFee, FeeFilters } from '../../types/fees';
import { feesApi, paymentsApi } from '../../services/feesApi';
import { toast } from 'react-toastify';
import './FeesTable.css';

interface FeesTableProps {
  filters: FeeFilters;
  onPaymentClick: (fee: StudentFee) => void;
  onRefresh?: () => void;
  refreshKey?: number;
}

const FeesTable: React.FC<FeesTableProps> = ({ filters, onPaymentClick, onRefresh, refreshKey }) => {
  const [fees, setFees] = useState<StudentFee[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [editingFeeId, setEditingFeeId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState<string>('');

  const fetchFees = useCallback(async (filterParams = filters) => {
    try {
      setLoading(true);
      const response = await feesApi.getAll(filterParams);
      setFees(response.data.fees);
      setPagination(response.data.pagination);
    } catch (error: any) {
      console.error('Error fetching fees:', error);
      toast.error(error.error || 'Failed to fetch fees');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees, refreshKey]);

  const handleDownloadReceipt = async (feeId: number) => {
    try {
      const response = await paymentsApi.getByFeeId(feeId);
      const latestPayment = response.data.payments[0];

      if (!latestPayment) {
        toast.info('No receipts available for this fee yet.');
        return;
      }

      await paymentsApi.downloadReceipt(latestPayment.id, latestPayment.receiptVoucherNumber);
    } catch (error: any) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const handleStatusUpdate = async (feeId: number, newStatus: 'pending' | 'paid' | 'overdue') => {
    try {
      await feesApi.updateStatus(feeId, newStatus);
      toast.success('Fee status updated successfully');
      fetchFees();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error('Error updating fee status:', error);
      toast.error(error.error || 'Failed to update fee status');
    }
  };

  const handleAmountEdit = (fee: StudentFee) => {
    setEditingFeeId(fee.id);
    setEditAmount(fee.amount.toString());
  };

  const handleAmountSave = async (feeId: number) => {
    const newAmount = parseFloat(editAmount);
    if (isNaN(newAmount) || newAmount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }

    try {
      await feesApi.update(feeId, { amount: newAmount });
      toast.success('Fee amount updated');
      setEditingFeeId(null);
      fetchFees();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error('Error updating fee amount:', error);
      toast.error('Failed to update fee amount');
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent, feeId: number) => {
    if (e.key === 'Enter') {
      handleAmountSave(feeId);
    } else if (e.key === 'Escape') {
      setEditingFeeId(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'status-pending',
      paid: 'status-paid',
      overdue: 'status-overdue'
    };

    return (
      <span className={`status-badge ${statusClasses[status as keyof typeof statusClasses]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDaysOverdue = (dueDate: string, status: string) => {
    if (status !== 'overdue') return null;
    
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const handlePageChange = (newPage: number) => {
    const updatedFilters = { ...filters, page: newPage };
    fetchFees(updatedFilters);
  };

  if (loading) {
    return (
      <div className="fees-table-loading">
        <div className="loading-spinner"></div>
        <p>Loading fees...</p>
      </div>
    );
  }

  return (
    <div className="fees-table-container">
      <div className="fees-table-header">
        <div className="table-info">
          <span className="total-count">
            {pagination.total} fee{pagination.total !== 1 ? 's' : ''} found
          </span>
        </div>
        <button 
          className="refresh-btn"
          onClick={() => fetchFees()}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {fees.length === 0 ? (
        <div className="no-fees">
          <div className="no-fees-icon">💰</div>
          <h3>No fees found</h3>
          <p>No fees match your current filters.</p>
        </div>
      ) : (
        <>
          <div className="fees-table-wrapper">
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Days Overdue</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fees.map((fee) => (
                  <tr key={fee.id} className={`fee-row ${fee.status}`}>
                    <td className="student-cell">
                      <div className="student-info">
                        <div className="student-name">{fee.studentName}</div>
                        {fee.student && (
                          <div className="student-email">{fee.student.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="description-cell">
                      <div className="fee-description">{fee.feeDescription}</div>
                    </td>
                    <td className="amount-cell">
                      {editingFeeId === fee.id ? (
                        <div className="amount-edit-group">
                          <input
                            type="number"
                            className="amount-edit-input"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            onKeyDown={(e) => handleAmountKeyDown(e, fee.id)}
                            autoFocus
                            step="0.01"
                            min="0"
                          />
                          <button className="amount-save-btn" onClick={() => handleAmountSave(fee.id)} title="Save">✓</button>
                          <button className="amount-cancel-btn" onClick={() => setEditingFeeId(null)} title="Cancel">✕</button>
                        </div>
                      ) : (
                        <span
                          className="amount editable"
                          onClick={() => fee.status !== 'paid' && handleAmountEdit(fee)}
                          title={fee.status !== 'paid' ? 'Click to edit amount' : ''}
                        >
                          {formatCurrency(fee.amount)}
                          {fee.status !== 'paid' && <span className="edit-icon">✏️</span>}
                        </span>
                      )}
                    </td>
                    <td className="date-cell">
                      <span className="due-date">{formatDate(fee.dueDate)}</span>
                    </td>
                    <td className="status-cell">
                      {getStatusBadge(fee.status)}
                    </td>
                    <td className="overdue-cell">
                      {fee.status === 'overdue' && (
                        <span className="days-overdue">
                          {getDaysOverdue(fee.dueDate, fee.status)} days
                        </span>
                      )}
                    </td>
                    <td className="created-cell">
                      <span className="created-date">{formatDate(fee.createdAt)}</span>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        {fee.status !== 'paid' && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => onPaymentClick(fee)}
                            title="Record Payment"
                          >
                            💳 Pay
                          </button>
                        )}
                        
                        {fee.status === 'paid' && (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDownloadReceipt(fee.id)}
                              title="Download Receipt"
                            >
                              🧾
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleStatusUpdate(fee.id, 'pending')}
                              title="Reopen (mark as pending)"
                            >
                              ↩️
                            </button>
                          </>
                        )}
                        
                        {fee.status === 'pending' && (
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleStatusUpdate(fee.id, 'overdue')}
                            title="Mark as Overdue"
                          >
                            ⚠️
                          </button>
                        )}
                        
                        {fee.status === 'overdue' && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleStatusUpdate(fee.id, 'pending')}
                            title="Mark as Pending"
                          >
                            🔄
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                ← Previous
              </button>
              
              <div className="pagination-info">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeesTable;
